import Ably from 'ably';

import {assert} from 'Asserts';
import {RealtimeMessageType} from 'realtime/Constants';

import {ablyErrorToRealtimeError, getAblyChannel, returnAblyChannel, CapabilityRequest} from './Ably';
import decryptRealtimeMessage from './decryptRealtimeMessage';
import RealtimeChannel, {
  PresenceMessage,
  PresenceMessageType,
  RealtimeChannelCapability,
  RealtimeError,
  RealtimeMessage,
} from './RealtimeChannel';

/**
 * Gets a reference to a realtime channel object with the granted capabilities. The returned reference is
 * unique to the caller and it is the caller's responsibility to manage and clean up when finished by calling
 * the channel's stop() method.
 *
 * @param channelName the name of the channel to get.
 * @param capabilities the set of realtime capabilities ('subscribe' and/or 'presence')
 * @returns a promise that will resolve to a Channel if authorization succeeds.
 */
export default async function getRealtimeChannel(
  channelName: string,
  capabilities: Array<RealtimeChannelCapability>
): Promise<RealtimeChannel> {
  const req: CapabilityRequest = {
    channelName,
    capabilities,
  };
  const ablyChannel = await getAblyChannel(req);
  let stopped = false;
  const presenceSubscriptions: Array<(msg: Ably.Types.PresenceMessage) => void> = [];
  const unsubscribeAllPresence = (): void => {
    presenceSubscriptions.forEach((handler) => {
      ablyChannel.presence.unsubscribe(handler);
    });
    while (presenceSubscriptions.length > 0) {
      presenceSubscriptions.pop();
    }
  };

  const channelSubscriptions: Array<(msg: Ably.Types.Message) => void> = [];
  const unsubscribeAllChannelSubscriptions = (): void => {
    channelSubscriptions.forEach((handler) => {
      ablyChannel.unsubscribe(handler);
    });
    while (channelSubscriptions.length > 0) {
      channelSubscriptions.pop();
    }
  };

  return Promise.resolve({
    presence: {
      enter: (callback?: (err?: RealtimeError | null) => void): void => {
        assert(!stopped, 'cannot access channel that has been stopped');
        let err: RealtimeError | null = null;
        ablyChannel.presence.enter((ablyError?: Ably.Types.ErrorInfo | null) => {
          if (ablyError) {
            err = ablyErrorToRealtimeError(ablyError);
          }
          if (callback) {
            callback(err);
          }
        });
      },
      leave: (callback?: (err?: RealtimeError | null) => void): void => {
        assert(!stopped, 'cannot access channel that has been stopped');
        let err: RealtimeError | null = null;
        ablyChannel.presence.leave((ablyError?: Ably.Types.ErrorInfo | null) => {
          if (ablyError) {
            err = ablyErrorToRealtimeError(ablyError);
          }
          if (callback) {
            callback(err);
          }
        });
      },
      subscribe(
        handler: (message: PresenceMessage) => void,
        subscribeCallback?: (err?: RealtimeError | null) => void
      ): void {
        assert(!stopped, 'cannot access channel that has been stopped');
        let err: RealtimeError | null = null;
        const ablyHandler = (ablyMsg: Ably.Types.PresenceMessage): void => {
          handler({
            type: ablyMsg.action as PresenceMessageType,
            userId: ablyMsg.clientId,
            connectionId: ablyMsg.connectionId,
          });
        };
        presenceSubscriptions.push(ablyHandler);
        ablyChannel.presence.subscribe(
          [PresenceMessageType.ENTER, PresenceMessageType.LEAVE, PresenceMessageType.PRESENT],
          ablyHandler,
          (ablyError?: Ably.Types.ErrorInfo | null) => {
            if (ablyError) {
              err = ablyErrorToRealtimeError(ablyError);
            }
            if (subscribeCallback) {
              subscribeCallback(err);
            }
          }
        );
      },
      unsubscribe(): void {
        assert(!stopped, 'cannot access channel that has been stopped');
        unsubscribeAllPresence();
      },
      get(callback: (err: RealtimeError | null, members: Array<PresenceMessage>) => void): void {
        ablyChannel.presence.get(
          (ablyError: Ably.Types.ErrorInfo | null, messages: Array<Ably.Types.PresenceMessage>): void => {
            if (ablyError) {
              callback(ablyErrorToRealtimeError(ablyError), []);
              return;
            }

            callback(
              null,
              messages.map((msg) => ({
                type: PresenceMessageType.PRESENT,
                userId: msg.clientId,
                connectionId: msg.connectionId,
              }))
            );
          }
        );
      },
    },
    subscribe: (
      handler: (message: RealtimeMessage) => void,
      subscribeCallback?: (err?: RealtimeError | null) => void
    ) => {
      assert(!stopped, 'cannot access channel that has been stopped');
      let err: RealtimeError | null = null;
      const ablyHandler = (ablyMsg: Ably.Types.Message): void => {
        decryptRealtimeMessage(channelName, {
          // We need to cast the message name to a RealtimeMessageType because the client could receive a message
          // that hasn't yet been defined in RealtimeMessageType.
          name: ablyMsg.name as RealtimeMessageType,
          data: ablyMsg.data,
        }).then((decryptedMessage) => {
          handler(decryptedMessage);
        });
      };

      channelSubscriptions.push(ablyHandler);
      ablyChannel.subscribe(ablyHandler, (ablyError?: Ably.Types.ErrorInfo | null) => {
        if (ablyError) {
          err = ablyErrorToRealtimeError(ablyError);
        }
        if (subscribeCallback) {
          subscribeCallback(err);
        }
      });
    },
    unsubscribe: () => {
      assert(!stopped, 'cannot access channel that has been stopped');
      unsubscribeAllChannelSubscriptions();
    },
    stop: () => {
      stopped = true;
      unsubscribeAllPresence();
      // Unsubscribe all other channels as well
      unsubscribeAllChannelSubscriptions();
      returnAblyChannel(req);
    },
  });
}
